<template>
  <div class="settings-shell" v-if="userStore.user && companyStore.company">
    <!-- Top bar -->
    <header class="settings-topbar">
      <div class="topbar-left">
        <h1 class="settings-h1">{{ $t('settings.title') }}</h1>
        <p class="settings-sub">{{ $t('settings.subtitle') }}</p>
      </div>
      <div class="account-chip">
        <i class="bi bi-person-circle"></i>
        <div class="account-meta">
          <span class="account-name">{{ userStore.user.UserName }} {{ userStore.user.UserSurname }}</span>
          <router-link to="/change-password?from=settings" class="account-link">
            <i class="bi bi-key"></i> {{ $t('settings.changePassword') }}
          </router-link>
        </div>
      </div>
    </header>

    <div class="settings-body">
      <!-- Left section nav -->
      <nav class="settings-nav" :aria-label="$t('settings.sectionsAria')">
        <button
          v-for="s in visibleSections"
          :key="s.id"
          type="button"
          class="nav-item"
          :class="{ active: activeSection === s.id }"
          @click="activeSection = s.id"
        >
          <i class="bi" :class="s.icon"></i>
          <span>{{ $t(`settings.sections.${s.id}`) }}</span>
        </button>
      </nav>

      <!-- Content pane -->
      <div class="settings-pane">

      <!-- ============================================================= -->
      <!-- STORE                                                         -->
      <!-- ============================================================= -->
      <section v-show="activeSection === 'store'" class="pane-group">

    <!-- ===== Store Benchmark — sector + staff cost, one Save ===== -->
    <!-- Sector drives the starting Emax/Cmax/TWT ceilings across the gauges,
         Pattern Matrix and Growth Potential math; staff cost is the €/hr the
         Capacity gauge uses to monetise wasted capacity. Both write
         CompanySettings via one updateCompanySettings call. -->
    <div v-if="isAdmin" class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-sliders"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.benchmark.title') }}</h2>
          <p class="sc-desc">{{ $t('settings.benchmark.desc') }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <!-- Retail sector -->
        <div class="block">
          <label class="form-label" for="companySectorSelect">{{ $t('settings.benchmark.sectorLabel') }}</label>
          <select
            id="companySectorSelect"
            class="form-control"
            v-model="selectedSector"
            :disabled="isSavingBenchmark"
          >
            <option value="" disabled>{{ $t('settings.benchmark.sectorPlaceholder') }}</option>
            <option v-for="opt in sectorOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>

          <div v-if="sectorPreview" class="sector-preview">
            <p v-if="sectorDescription" class="sector-description">
              {{ sectorDescription }}
            </p>
            <div class="sector-ceilings">
              <i18n-t keypath="settings.benchmark.bestPractices" scope="global"><template #sector><strong>{{ sectorPreview.label }}</strong></template></i18n-t>
              E<sub>max</sub> {{ formatPct(sectorPreview.Emax) }} ·
              C<sub>max</sub> {{ formatPct(sectorPreview.Cmax) }} ·
              TWT {{ sectorPreview.twt }} min
            </div>
          </div>
        </div>

        <!-- Checkout staff cost override -->
        <div class="block">
          <label class="form-label" for="staffCostInput">{{ $t('settings.benchmark.staffCostLabel') }}</label>
          <i18n-t v-if="selectedSector" keypath="settings.benchmark.defaultFor" tag="p" class="field-help" scope="global">
            <template #sector><strong>{{ sectorPreview ? sectorPreview.label : selectedSector }}</strong></template>
            <template #cost><strong>{{ $t('settings.benchmark.costPerHour', { n: fmtCost(sectorDefaultStaffCost) }) }}</strong></template>
          </i18n-t>
          <i18n-t v-else keypath="settings.benchmark.pickSectorFirst" tag="p" class="field-help" scope="global">
            <template #cost><strong>{{ $t('settings.benchmark.costPerHour', { n: fmtCost(sectorDefaultStaffCost) }) }}</strong></template>
          </i18n-t>
          <div class="input-group narrow">
            <span class="input-group-text">€</span>
            <input
              type="number"
              id="staffCostInput"
              class="form-control"
              v-model="staffCostInput"
              min="0"
              step="0.5"
              :placeholder="String(sectorDefaultStaffCost)"
              :disabled="isSavingBenchmark"
            />
            <span class="input-group-text">{{ $t('settings.benchmark.perHour') }}</span>
          </div>
        </div>

        <div class="form-actions">
          <div v-if="isSavingBenchmark" class="saving">
            <div class="saver"></div><span>{{ $t('settings.saving') }}</span>
          </div>
          <button
            class="btn-solid ok"
            @click="saveBenchmark"
            :disabled="!isBenchmarkChanged || isSavingBenchmark"
          >
            <i class="bi bi-save"></i> {{ $t('settings.benchmark.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Store language — the default for everyone in this store who hasn't
         picked their own (each person can, at the bottom of the menu). ===== -->
    <div v-if="isAdmin" class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-translate"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('storeLanguage.title') }}</h2>
          <p class="sc-desc">{{ $t('storeLanguage.desc') }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <div class="block">
          <label class="form-label" for="storeLanguageSelect">{{ $t('storeLanguage.label') }}</label>
          <select
            id="storeLanguageSelect"
            class="form-control lang-select"
            v-model="storeLanguage"
            :disabled="isSavingLanguage"
          >
            <option v-for="code in LANGUAGES" :key="code" :value="code">{{ $t(`language.${code}`) }}</option>
          </select>
        </div>

        <div class="form-actions">
          <div v-if="isSavingLanguage" class="saving">
            <div class="saver"></div><span>{{ $t('settings.saving') }}</span>
          </div>
          <button
            class="btn-solid ok"
            @click="saveStoreLanguage"
            :disabled="!isStoreLanguageChanged || isSavingLanguage"
          >
            <i class="bi bi-save"></i> {{ $t('storeLanguage.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Opening Hours Component ===== -->
    <opening-hours v-if="isAdmin"></opening-hours>

    <!-- ===== Location & Calendar — GPS (weather) + holiday calendars ===== -->
    <location-calendar-settings v-if="isAdmin"></location-calendar-settings>

    <!-- ===== Device Connection — reference strip (read-only) ===== -->
    <div v-if="isAdmin" class="device-strip">
      <i class="bi bi-hdd-network"></i>
      <span class="device-label">{{ $t('settings.connectedDevice') }}</span>
      <code class="device-url">{{ companyStore.company.SocketioDeviceURL || '—' }}</code>
    </div>

      </section>

      <!-- ============================================================= -->
      <!-- ALLEN & REPORTS                                               -->
      <!-- ============================================================= -->
      <section v-show="activeSection === 'allen'" class="pane-group">

    <!-- ===== Allen Coach — Store Goal & Context ===== -->
    <!-- Company-level coach inputs. The store context lives in
         CompanySettings.coachProfile (saved via updateCompanySettings, so it
         never clobbers sector / staffCostPerHour); goals live in the
         store_goals table via the /coach/goals endpoints. -->
    <div v-if="isAdmin" class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-stars"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.coach.title') }}</h2>
          <p class="sc-desc">{{ $t('settings.coach.desc') }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <!-- Master switch: generates the nightly digest for this store -->
        <div class="block">
          <label style="display:flex; align-items:center; gap:.5rem; cursor:pointer;">
            <input type="checkbox" v-model="coachEnabled" @change="saveCoachEnabled" :disabled="isSavingEnabled" />
            <span class="form-label" style="margin:0;">{{ $t('settings.coach.enable') }}</span>
          </label>
          <p class="field-help">
            {{ $t('settings.coach.enableHelp') }}
          </p>
        </div>

        <!-- Store context (CompanySettings.coachProfile) -->
        <div class="block">
          <label class="form-label" for="coachContext">{{ $t('settings.coach.contextLabel') }}</label>
          <p class="field-help">
            {{ $t('settings.coach.contextHelp') }}
          </p>
          <textarea
            id="coachContext"
            class="form-control"
            rows="4"
            v-model="coachContext"
            :disabled="isSavingProfile"
            :placeholder="$t('settings.coach.contextPlaceholder')"
          ></textarea>
        </div>

        <div class="block">
          <label class="form-label" for="coachTone">{{ $t('settings.coach.toneLabel') }}</label>
          <select id="coachTone" class="form-control" v-model="coachTone" :disabled="isSavingProfile">
            <option value="terse">{{ $t('settings.coach.tones.terse') }}</option>
            <option value="balanced">{{ $t('settings.coach.tones.balanced') }}</option>
            <option value="detailed">{{ $t('settings.coach.tones.detailed') }}</option>
          </select>
        </div>

        <div class="form-actions">
          <div v-if="isSavingProfile" class="saving">
            <div class="saver"></div><span>{{ $t('settings.saving') }}</span>
          </div>
          <button
            class="btn-solid ok"
            @click="saveCoachProfile"
            :disabled="!isProfileChanged || isSavingProfile"
          >
            <i class="bi bi-save"></i> {{ $t('settings.coach.saveContext') }}
          </button>
        </div>

        <!-- Goals (store_goals table) -->
        <details class="sub">
          <summary>
            <i class="bi bi-flag"></i> {{ $t('settings.goals.title') }}
            <span class="count" v-if="activeGoals.length">· {{ activeGoals.length }}</span>
            <i class="bi bi-chevron-right chev"></i>
          </summary>
          <div class="sub-body">
          <p class="field-help">
            {{ $t('settings.goals.help') }}
          </p>

          <ul v-if="activeGoals.length" style="list-style:none; padding:0; margin:0 0 .75rem;">
            <li
              v-for="goal in activeGoals"
              :key="goal.GoalId"
              style="display:flex; justify-content:space-between; align-items:center; gap:.5rem; padding:.4rem 0; border-bottom:1px solid #eee;"
            >
              <span>
                {{ goalTitle(goal) }}
                <small v-if="goal.GoalData.metric" style="color:#888;">
                  {{ $t('settings.goals.focus', { metric: goalMetricLabel(goal.GoalData.metric) }) }}<template v-if="goal.GoalData.zone"> · {{ goal.GoalData.zone }}</template>
                </small>
              </span>
              <Tooltip :text="$t('settings.goals.doneTooltip')" placement="left" cursor="pointer">
                <button class="btn-solid" @click="achieveGoal(goal.GoalId)">
                  <i class="bi bi-check2-circle"></i> {{ $t('settings.goals.done') }}
                </button>
              </Tooltip>
            </li>
          </ul>
          <p v-else class="field-help"><em>{{ $t('settings.goals.empty') }}</em></p>

          <div class="input-group">
            <select class="form-control" v-model="newGoalMetric" :disabled="isAddingGoal">
              <option value="" disabled>{{ $t('settings.goals.chooseFocus') }}</option>
              <option value="engagement">{{ $t('settings.goals.options.engagement') }}</option>
              <option value="conversion">{{ $t('settings.goals.options.conversion') }}</option>
              <option value="reach">{{ $t('settings.goals.options.reach') }}</option>
              <option value="traffic">{{ $t('settings.goals.options.traffic') }}</option>
            </select>
            <select class="form-control" v-model="newGoalZone"
                    :disabled="isAddingGoal || newGoalMetric === 'traffic'">
              <option value="">{{ newGoalMetric === 'traffic' ? $t('settings.goals.wholeStore') : $t('settings.goals.storeWide') }}</option>
              <option v-for="z in goalZoneOptions" :key="z" :value="z">{{ z }}</option>
            </select>
            <button
              class="btn-solid ok"
              @click="addGoal"
              :disabled="!newGoalMetric || isAddingGoal"
            >
              <i class="bi bi-plus-lg"></i> {{ $t('settings.goals.add') }}
            </button>
          </div>
          <p class="field-help" style="margin-top:0;">
            {{ $t('settings.goals.addHelp') }}
          </p>
          </div>
        </details>

        <!-- Durable memory (conversation_memory) — what Allen has learned -->
        <details class="sub">
          <summary>
            <i class="bi bi-lightbulb"></i> {{ $t('settings.memory.title') }}
            <span class="count" v-if="coachFacts.length">· {{ coachFacts.length }}</span>
            <i class="bi bi-chevron-right chev"></i>
          </summary>
          <div class="sub-body">
          <p class="field-help">
            {{ $t('settings.memory.help') }}
          </p>

          <ul v-if="coachFacts.length" style="list-style:none; padding:0; margin:0 0 .75rem;">
            <li
              v-for="m in coachFacts"
              :key="m.MemoryId"
              style="display:flex; justify-content:space-between; align-items:center; gap:.5rem; padding:.4rem 0; border-bottom:1px solid #eee;"
            >
              <span>{{ m.Data.fact }}</span>
              <Tooltip :text="$t('settings.memory.forgetTooltip')" placement="left" cursor="pointer">
                <button class="btn-solid" @click="forgetFact(m.MemoryId)">
                  <i class="bi bi-x-lg"></i> {{ $t('settings.memory.forget') }}
                </button>
              </Tooltip>
            </li>
          </ul>
          <p v-else class="field-help"><em>{{ $t('settings.memory.empty') }}</em></p>

          <div class="input-group">
            <input
              type="text"
              class="form-control"
              v-model="newFact"
              :disabled="isAddingFact"
              maxlength="300"
              :placeholder="$t('settings.memory.placeholder')"
              @keyup.enter="addFact"
            />
            <button
              class="btn-solid ok"
              @click="addFact"
              :disabled="!newFact.trim() || isAddingFact"
            >
              <i class="bi bi-plus-lg"></i> {{ $t('settings.memory.teach') }}
            </button>
          </div>
          </div>
        </details>
      </div>
    </div>

    <!-- ===== Email Preferences — period reports + Allen notifications, one Save.
         Both groups persist through updatePreferences() (same UserSettings blob),
         so they share a single card and a single save. ===== -->
    <div class="setting-card" :style="{ '--accent': '#B2C149' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-envelope"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.email.title') }}</h2>
          <p class="sc-desc">
            <i18n-t keypath="settings.email.desc" scope="global"><template #store><strong>{{ companyStore.company.CompanyName }}</strong></template></i18n-t>
            <template v-if="userStore.companies && userStore.companies.length > 1">
              {{ $t('settings.email.otherStores') }}
            </template>
          </p>
        </div>
      </div>

      <div class="setting-card-body">
        <!-- End-of-period reports -->
        <div class="block">
          <label class="form-label">{{ $t('settings.email.periodReports') }}</label>
          <div class="checklist">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="endOfDayCheckbox" v-model="userSettings.SendEndOfDay">
              <label class="form-check-label" for="endOfDayCheckbox">{{ $t('settings.email.endOfDay') }}</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="endOfWeekCheckbox" v-model="userSettings.SendEndOfWeek">
              <label class="form-check-label" for="endOfWeekCheckbox">{{ $t('settings.email.endOfWeek') }}</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="endOfMonthCheckbox" v-model="userSettings.SendEndOfMonth">
              <label class="form-check-label" for="endOfMonthCheckbox">{{ $t('settings.email.endOfMonth') }}</label>
            </div>
          </div>
        </div>

        <!-- Allen proactive notifications -->
        <div class="block">
          <label class="switch-row">
            <input type="checkbox" v-model="notifyPrefs.CoachEmailEnabled" :disabled="isSavingEmailPrefs" />
            <span class="form-label" style="margin:0;">{{ $t('settings.email.proactive') }}</span>
          </label>
          <p class="field-help">{{ $t('settings.email.proactiveHelp') }}</p>
          <div class="checklist indent" :class="{ 'is-muted': !notifyPrefs.CoachEmailEnabled }">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="prefHigh"
                     v-model="notifyPrefs.CoachEmailHigh"
                     :disabled="!notifyPrefs.CoachEmailEnabled || isSavingEmailPrefs">
              <label class="form-check-label" for="prefHigh"><strong>{{ $t('settings.email.high') }}</strong> {{ $t('settings.email.highHelp') }}</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="prefWarn"
                     v-model="notifyPrefs.CoachEmailWarning"
                     :disabled="!notifyPrefs.CoachEmailEnabled || isSavingEmailPrefs">
              <label class="form-check-label" for="prefWarn"><strong>{{ $t('settings.email.warning') }}</strong> {{ $t('settings.email.warningHelp') }}</label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="prefGood"
                     v-model="notifyPrefs.CoachEmailGood"
                     :disabled="!notifyPrefs.CoachEmailEnabled || isSavingEmailPrefs">
              <label class="form-check-label" for="prefGood"><strong>{{ $t('settings.email.good') }}</strong> {{ $t('settings.email.goodHelp') }}</label>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <div v-if="isSavingEmailPrefs" class="saving">
            <div class="saver"></div><span>{{ $t('settings.saving') }}</span>
          </div>
          <button
            class="btn-solid ok"
            @click="saveEmailPrefs"
            :disabled="!isEmailPrefsChanged || isSavingEmailPrefs"
          >
            <i class="bi bi-save"></i> {{ $t('settings.email.save') }}
          </button>
        </div>
      </div>
    </div>

    <!-- ===== Push Notifications (docs/15 C) — device-level + Allen push ===== -->
    <div class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-phone-vibrate"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.push.title') }}</h2>
          <p class="sc-desc">{{ $t('settings.push.desc') }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <!-- Install (Chrome/Android/desktop) is step 1 when available: installing
             first, then enabling push, avoids the install dialog and the
             notification-permission prompt colliding. iOS gets the manual
             Share -> Add to Home Screen coaching below instead, since Safari fires no
             beforeinstallprompt. -->
        <div class="block" v-if="pushShouldInstallFirst">
          <label class="form-label" style="display:block;">
            <span v-if="pushSupported" class="step-num">1</span>{{ $t('settings.push.install') }}
          </label>
          <p class="field-help">
            {{ $t('settings.push.installHelp') }}
          </p>
          <button class="btn-solid ok" @click="installApp">
            <i class="bi bi-download"></i> {{ $t('settings.push.install') }}
          </button>
        </div>
        <p v-else-if="pushIsStandalone || pushInstalled" class="field-help">
          <i class="bi bi-check-circle-fill" style="color:#6C9046;"></i>
          {{ $t('settings.push.installed') }}
        </p>

        <!-- unsupported browser -->
        <p v-if="!pushSupported" class="field-help">
          {{ $t('settings.push.unsupported') }}
        </p>

        <!-- iOS must be installed to the home screen first -->
        <div v-else-if="pushNeedsInstall" class="push-hint">
          <i18n-t keypath="settings.push.iosHint" tag="p" class="field-help" style="margin:0;" scope="global">
            <template #device><strong>{{ $t('settings.push.iosDevice') }}</strong></template>
            <template #share><i class="bi bi-box-arrow-up"></i> <strong>{{ $t('settings.push.iosShare') }}</strong></template>
            <template #addToHome><strong>{{ $t('settings.push.iosAddToHome') }}</strong></template>
          </i18n-t>
        </div>

        <!-- blocked in browser settings -->
        <p v-else-if="pushPermission === 'denied'" class="field-help">
          {{ $t('settings.push.blocked') }}
        </p>

        <!-- the enable/disable control -->
        <template v-else>
          <div class="block"
               :class="{ 'is-muted': pushShouldInstallFirst && !pushSubscribed,
                         'needs-reenable': pushNeedsReenable }">
            <label class="switch-row" style="cursor:default;">
              <span class="form-label" style="margin:0;">
                <span v-if="pushSupported && pushShouldInstallFirst" class="step-num">2</span>
                <i v-if="pushNeedsReenable" class="bi bi-exclamation-triangle-fill" style="color:#C77700; margin-right:4px;"></i>{{ $t('settings.push.thisDevice') }}
              </span>
            </label>
            <p class="field-help">
              {{ pushSubscribed
                  ? $t('settings.push.subscribed')
                  : (pushNeedsReenable
                      ? $t('settings.push.needsReenable')
                      : (pushShouldInstallFirst
                          ? $t('settings.push.installFirst')
                          : $t('settings.push.turnOn'))) }}
            </p>
            <button
              v-if="!pushSubscribed"
              :class="(pushShouldInstallFirst && !pushNeedsReenable) ? 'btn-outline' : 'btn-solid ok'"
              @click="enableDeviceNotifications"
              :disabled="pushBusy"
            >
              <i class="bi" :class="pushNeedsReenable ? 'bi-arrow-clockwise' : 'bi-bell'"></i>
              {{ pushNeedsReenable ? $t('settings.push.reenable') : $t('settings.push.enable') }}
            </button>
            <button
              v-else
              class="btn-outline"
              @click="disableDeviceNotifications"
              :disabled="pushBusy"
            >
              <i class="bi bi-bell-slash"></i> {{ $t('settings.push.disable') }}
            </button>
          </div>

          <!-- Allen push — admin only, and only meaningful once a device is on -->
          <div class="block" v-if="isAdmin" :class="{ 'is-muted': !pushSubscribed }">
            <label class="switch-row">
              <input
                type="checkbox"
                v-model="coachPush"
                @change="toggleCoachPush"
                :disabled="!pushSubscribed || isSavingCoachPush"
              />
              <span class="form-label" style="margin:0;">{{ $t('settings.push.coachPush') }}</span>
            </label>
            <p class="field-help">
              {{ $t('settings.push.coachPushHelp') }}
              <template v-if="!pushSubscribed">{{ $t('settings.push.enableDeviceFirst') }}</template>
            </p>
          </div>
        </template>
      </div>
    </div>

    <!-- ===== Real-time Alerts (docs/15 channel 2) ===== -->
    <div class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-broadcast-pin"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.realtime.title') }}</h2>
          <p class="sc-desc">{{ $t('settings.realtime.desc', { store: companyStore.company.CompanyName }) }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <!-- SCOPE first, then delivery. The zones govern the bell, the pop-up AND
             push, so they can't hang off any one delivery toggle — reading them as
             "the push zones" was purely an ordering accident. -->
        <div class="block">
          <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
            <label class="form-label" style="margin:0;">{{ $t('settings.realtime.zonesLabel') }}</label>
            <span class="zone-actions">
              <button type="button" class="linklike" @click="selectAllZones" :disabled="isSavingRealtime">{{ $t('settings.realtime.all') }}</button>
              <span class="sep">·</span>
              <button type="button" class="linklike" @click="clearZones" :disabled="isSavingRealtime">{{ $t('settings.realtime.none') }}</button>
            </span>
          </div>
          <p class="field-help">{{ $t('settings.realtime.zonesHelp') }}</p>

          <div v-if="allZones.length" class="zone-checklist">
            <label v-for="z in allZones" :key="z.id" class="zone-item">
              <input type="checkbox" :checked="zoneChecked(z.id)" @change="toggleZone(z.id)" :disabled="isSavingRealtime" />
              <span>{{ z.name }}</span>
            </label>
          </div>
          <p v-else class="field-help"><em>{{ $t('settings.realtime.noZonesConfigured') }}</em></p>
        </div>

        <div class="block" :class="{ 'is-muted': noZones }">
          <label class="form-label" style="display:block; margin-bottom:6px;">{{ $t('settings.realtime.howLabel') }}</label>
          <p v-if="noZones" class="field-help" style="margin-top:0;">
            {{ $t('settings.realtime.noZones') }}
          </p>
          <p v-else class="field-help" style="margin-top:0;">
            {{ $t('settings.realtime.bellNote') }}
          </p>

          <label class="switch-row" style="margin-top:8px;">
            <input type="checkbox" v-model="realtimeToast" :disabled="isSavingRealtime || noZones" />
            <span>{{ $t('settings.realtime.toast') }}</span>
          </label>

          <label class="switch-row" style="margin-top:8px;"
                 :class="{ 'is-muted': !pushSubscribed }">
            <input
              type="checkbox"
              v-model="realtimePush"
              :disabled="!pushSubscribed || isSavingRealtime || noZones"
            />
            <span>{{ $t('settings.realtime.push') }}</span>
          </label>
          <i18n-t v-if="!pushSubscribed && !noZones" keypath="settings.realtime.pushNeedsDevice" tag="p" class="field-help" style="margin-top:6px;" scope="global">
            <template #push><strong>{{ $t('settings.realtime.pushCard') }}</strong></template>
          </i18n-t>
        </div>

        <div class="form-actions">
          <div v-if="isSavingRealtime" class="saving">
            <div class="saver"></div><span>{{ $t('settings.saving') }}</span>
          </div>
          <button
            class="btn-solid ok"
            @click="saveRealtime"
            :disabled="!isRealtimeChanged || isSavingRealtime"
          >
            <i class="bi bi-save"></i> {{ $t('settings.realtime.save') }}
          </button>
        </div>
      </div>
    </div>

      </section>

      <!-- ============================================================= -->
      <!-- TEAM                                                          -->
      <!-- ============================================================= -->
      <section v-show="activeSection === 'team'" class="pane-group">

    <!-- ===== User Management ===== -->
    <div v-if="isAdmin" class="setting-card" :style="{ '--accent': '#4F7EB3' }">
      <div class="setting-card-head">
        <span class="sc-accent"><i class="bi bi-people-fill"></i></span>
        <div class="sc-heading">
          <h2 class="sc-title">{{ $t('settings.team.title') }}</h2>
          <p class="sc-desc">{{ $t('settings.team.desc', { store: companyStore.company.CompanyName }) }}</p>
        </div>
      </div>

      <div class="setting-card-body">
        <div class="table-container">
          <table class="user-table">
            <thead>
              <tr>
                <th>{{ $t('settings.team.name') }}</th>
                <th>{{ $t('settings.team.surname') }}</th>
                <th>{{ $t('settings.team.email') }}</th>
                <th>{{ $t('settings.team.role') }}</th>
                <th>{{ $t('settings.team.actions') }}</th>
              </tr>
            </thead>
            <tbody>
              <!-- Existing users -->
              <tr v-for="(item, index) in userList" :key="index">
                <td>
                  <template v-if="item.editing">
                    <input type="text" class="form-control small" v-model="item.UserName" />
                  </template>
                  <template v-else>
                    <b>{{ item.UserName }}</b>
                  </template>
                </td>
                <td>
                  <template v-if="item.editing">
                    <input type="text" class="form-control small" v-model="item.UserSurname" />
                  </template>
                  <template v-else>
                    <b>{{ item.UserSurname }}</b>
                  </template>
                </td>
                <td>
                  <template v-if="item.editing">
                    <input
                      type="text"
                      class="form-control small"
                      v-model="item.UserEmail"
                      @blur="item.emailTouched = true"
                      :class="{ 'is-invalid': item.emailTouched && !isEmailValid(item.UserEmail) && item.UserEmail !== '' }"
                    />
                    <div
                      v-if="item.emailTouched && !isEmailValid(item.UserEmail) && item.UserEmail !== ''"
                      class="invalid-feedback"
                    >
                      {{ $t('settings.team.invalidEmail') }}
                    </div>
                  </template>
                  <template v-else>
                    {{ item.UserEmail }}
                  </template>
                </td>
                <td>
                  <template v-if="item.editing">
                    <select class="form-select small" v-model="item.UserRole">
                      <option value="User">{{ roleLabel('User') }}</option>
                      <option value="Key User">{{ roleLabel('Key User') }}</option>
                      <option value="Admin">{{ roleLabel('Admin') }}</option>
                    </select>
                  </template>
                  <template v-else>
                    {{ roleLabel(item.UserRole) }}
                  </template>
                </td>
                <td class="actions">
                  <div v-if="isLoading" class="loader"></div>
                  <template v-else>
                    <div v-if="item.editing">
                      <button class="btn-outline" @click="cancelEditUser(item)">
                        <i class="bi bi-x-lg"></i>
                      </button>
                      <button
                        class="btn-solid ok"
                        @click="updateUser(item.UserId)"
                        :disabled="!hasChanges(item)"
                      >
                        <i class="bi bi-check-lg"></i>
                      </button>
                    </div>
                    <div v-else>
                      <Tooltip :text="$t('settings.team.editTooltip')" placement="left" cursor="pointer">
                        <button class="btn-outline" @click="editUser(item)">
                          <i class="bi bi-person"></i>
                        </button>
                      </Tooltip>
                      <Tooltip :text="$t('settings.team.resendTooltip')" placement="left" cursor="pointer">
                        <button
                          class="btn-outline"
                          @click="resendInvite(item)"
                          :disabled="resendingUserId === item.UserId"
                        >
                          <span v-if="resendingUserId === item.UserId" class="mini-spin"></span>
                          <i v-else class="bi bi-envelope-arrow-up"></i>
                        </button>
                      </Tooltip>
                      <Tooltip :text="$t('settings.team.removeTooltip')" placement="left" cursor="pointer">
                        <button class="btn-outline danger" @click="deleteUser(item.UserEmail)">
                          <i class="bi bi-person-x"></i>
                        </button>
                      </Tooltip>
                    </div>
                  </template>
                </td>
              </tr>

              <!-- New user row -->
              <tr>
                <td>
                  <input v-if="addingUser" type="text" class="form-control small" v-model="newUserName" :placeholder="$t('settings.team.name')" />
                </td>
                <td>
                  <input v-if="addingUser" type="text" class="form-control small" v-model="newUserSurname" :placeholder="$t('settings.team.surname')" />
                </td>
                <td>
                  <div v-if="addingUser">
                    <input
                      type="text"
                      class="form-control small"
                      v-model="newUserEmail"
                      @blur="newUserEmailTouched = true"
                      :class="{ 'is-invalid': newUserEmailTouched && !isEmailValid(newUserEmail) && newUserEmail !== '' }"
                      :placeholder="$t('settings.team.email')"
                    />
                    <div
                      v-if="newUserEmailTouched && !isEmailValid(newUserEmail) && newUserEmail !== ''"
                      class="invalid-feedback"
                    >
                      {{ $t('settings.team.invalidEmail') }}
                    </div>
                  </div>
                </td>
                <td>
                  <select v-if="addingUser" class="form-select small" v-model="newUserRole">
                    <option value="User">{{ roleLabel('User') }}</option>
                    <option value="Key User">{{ roleLabel('Key User') }}</option>
                    <option value="Admin">{{ roleLabel('Admin') }}</option>
                  </select>
                </td>
                <td class="actions">
                  <div v-if="isLoading" class="loader"></div>
                  <div v-else>
                    <button
                      v-if="!addingUser"
                      class="btn-outline"
                      @click="toggleAddUser"
                    >
                      <i class="bi bi-plus-lg"></i> {{ $t('settings.team.addTeammate') }}
                    </button>
                    <!-- Super-admin only, and that's a security boundary: a store
                         admin only knows their own people, so letting them test
                         whether an email exists would be an account-enumeration
                         oracle. Storalytic staff use this to place an existing
                         person (a partner, or themselves) into a store. -->
                    <button
                      v-if="!addingUser && userStore.isSuperAdmin"
                      class="btn-outline"
                      @click="toggleAddExisting"
                    >
                      <i class="bi bi-person-check"></i> {{ $t('settings.team.addExisting') }}
                    </button>
                    <div v-else>
                      <button class="btn-outline" @click="cancelAddUser">
                        <i class="bi bi-x-lg"></i>
                      </button>
                      <button class="btn-solid ok" @click="addUser(newUserEmail)">
                        <i class="bi bi-person-plus"></i>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ===== Add an EXISTING Storalytic user (super-admin only) ===== -->
        <div v-if="addingExisting && userStore.isSuperAdmin" class="add-existing">
          <i18n-t keypath="settings.team.existingHelp" tag="p" class="field-help" scope="global">
            <template #account><strong>{{ $t('settings.team.existingAccount') }}</strong></template>
            <template #store>{{ companyStore.company.CompanyName }}</template>
          </i18n-t>
          <div class="input-group">
            <input
              type="text"
              class="form-control small"
              v-model="existingEmail"
              :placeholder="$t('settings.team.existingPlaceholder')"
              @keyup.enter="addExistingMember"
            />
            <select class="form-select small" v-model="existingRole">
              <option value="User">{{ roleLabel('User') }}</option>
              <option value="Key User">{{ roleLabel('Key User') }}</option>
              <option value="Admin">{{ roleLabel('Admin') }}</option>
            </select>
            <button class="btn-outline" @click="cancelAddExisting">{{ $t('settings.team.cancel') }}</button>
            <button
              class="btn-solid ok"
              @click="addExistingMember"
              :disabled="!isEmailValid(existingEmail) || isAddingExisting"
            >
              <span v-if="isAddingExisting" class="mini-spin"></span>
              <template v-else><i class="bi bi-person-check"></i> {{ $t('settings.team.add') }}</template>
            </button>
          </div>
        </div>
      </div>
    </div>

      </section>
      </div><!-- /settings-pane -->
    </div><!-- /settings-body -->
  </div><!-- /settings-shell -->
</template>


<script>
import { ref, computed, onBeforeMount, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useToast } from 'vue-toastification'

import OpeningHours from '@/components/OpeningHours.vue'
import LocationCalendarSettings from '@/components/LocationCalendarSettings.vue'

import { useUserStore } from '@/stores/UserStore'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'

// Source of truth for the Company Benchmark Sector picker — every option,
// the per-sector starting Emax/Cmax/TWT, and the fallback chain live in
// ceilingsRef.js (the JS side is canonical; the backend runtime reads the
// generated signals/prioritization_config.json).
import {
    SECTORS,
    getZoneTypeDefault,
    getTwt,
} from '@/prioritization/ceilingsRef'

// Per-sector hourly staff-cost defaults — sourced from the analytics
// math layer so this picker shows exactly what the Capacity gauge
// would use when no company-level override is set.
import { CAPACITY_VALUE_SETTINGS } from '@/prioritization/settings'

import { MDBTabs, MDBTabNav, MDBTabContent, MDBTabItem, MDBTabPane } from 'mdb-vue-ui-kit'

import Tooltip from '@/components/ui/Tooltip.vue'
import { SUPPORTED as LANGUAGES, i18n } from '@/i18n'
import { formatNumber } from '@/i18n/format'

import { usePush } from '@/composables/usePush'


export default {
    name: 'Settings',

    components: {
        MDBTabs,
        MDBTabNav,
        MDBTabContent,
        MDBTabItem,
        MDBTabPane,
        OpeningHours,
        LocationCalendarSettings,
        Tooltip,
    },

    props: {

    },

    setup(props, { emit }) {
        const toast = useToast()

        const userStore = useUserStore()
        const companyStore = useCompanyStore()
        const detectionZoneStore = useDetectionZoneStore()

        const router = useRouter()

        const userList = ref([])
        const addingUser = ref(false)
        const newUserName = ref('')
        const newUserSurname = ref('')
        const newUserEmail = ref('')
        const newUserRole = ref('User')

        const newUserEmailTouched = ref(false)

        const userData = ref({
            UserName: '',
            UserSurname: '',
            UserEmail: '',
            CompanyId: '',
            UserRole: '',
            UserSettings: {}
        })

        const userSettings = ref({
            SendEndOfDay:   false,
            SendEndOfWeek:  false,
            SendEndOfMonth: false
        })
        const originalUserSettings = ref({})
        const isMailSettingsChanged = ref(false)
        const isSavingMailSettings = ref(false)

        // ---------- Allen notification-email preferences (phase 3) ----------
        // Per-user; persisted merge-safe via PUT /user/notification-preferences
        // (NOT the mail-toggle path). Master switch ships OFF (opt-in).
        const notifyPrefs = ref({
            CoachEmailEnabled: false,
            CoachEmailHigh:    true,
            CoachEmailWarning: true,
            CoachEmailGood:    false,
        })
        const originalNotifyPrefs = ref({})
        const isSavingNotifyPrefs = ref(false)
        const isNotifyPrefsChanged = computed(() =>
            JSON.stringify(notifyPrefs.value) !== JSON.stringify(originalNotifyPrefs.value)
        )
        const saveNotifyPrefs = async () => {
            if (!isNotifyPrefsChanged.value || isSavingNotifyPrefs.value) return
            isSavingNotifyPrefs.value = true
            try {
                await userStore.updatePreferences({ ...notifyPrefs.value })
                originalNotifyPrefs.value = JSON.parse(JSON.stringify(notifyPrefs.value))
                toast.success(i18n.global.t('settings.email.notifySaved'))
            } catch (e) {
                console.error('Failed to save notification preferences', e)
                toast.error(i18n.global.t('settings.email.notifySaveFailed'))
            } finally {
                isSavingNotifyPrefs.value = false
            }
        }

        // Unified save for the merged "Email Preferences" card — period reports
        // AND Allen notifications persist through the same updatePreferences call,
        // so the card has one Save covering both groups.
        const isSavingEmailPrefs = ref(false)
        const isEmailPrefsChanged = computed(() =>
            isMailSettingsChanged.value || isNotifyPrefsChanged.value
        )
        const saveEmailPrefs = async () => {
            if (!isEmailPrefsChanged.value || isSavingEmailPrefs.value) return
            isSavingEmailPrefs.value = true
            try {
                await userStore.updatePreferences({
                    SendEndOfDay:   userSettings.value.SendEndOfDay,
                    SendEndOfWeek:  userSettings.value.SendEndOfWeek,
                    SendEndOfMonth: userSettings.value.SendEndOfMonth,
                    ...notifyPrefs.value,
                })
                originalUserSettings.value = JSON.parse(JSON.stringify(userSettings.value))
                originalNotifyPrefs.value  = JSON.parse(JSON.stringify(notifyPrefs.value))
                isMailSettingsChanged.value = false
                toast.success(i18n.global.t('settings.email.saved'))
            } catch (e) {
                console.error('Failed to save email preferences', e)
                toast.error(i18n.global.t('settings.email.saveFailed'))
            } finally {
                isSavingEmailPrefs.value = false
            }
        }

        // ---------- Real-time alerts (docs/15 channel 2) ----------
        // Two things: the in-portal pop toggle (RealtimeToast, a per-company pref)
        // and which zones alert me (the zone-subscriptions table). "All selected"
        // = the opt-out default (nothing stored, incl. zones added later).
        const realtimeToast = ref(true)
        const originalRealtimeToast = ref(true)
        const realtimePush = ref(false)            // buzz a closed device (docs/15 D)
        const originalRealtimePush = ref(false)
        const zoneSelected = ref(new Set())        // selected DetectionZoneIds
        const originalZoneSelected = ref(new Set())
        const isSavingRealtime = ref(false)

        const allZones = computed(() => (detectionZoneStore.detectionzones || [])
            .filter(z => z && z.DetectionZoneId && z.DetectionZoneName)
            .map(z => ({ id: z.DetectionZoneId, name: z.DetectionZoneName }))
        )
        const _setsEqual = (a, b) => a.size === b.size && [...a].every(x => b.has(x))
        const isRealtimeChanged = computed(() =>
            realtimeToast.value !== originalRealtimeToast.value ||
            realtimePush.value !== originalRealtimePush.value ||
            !_setsEqual(zoneSelected.value, originalZoneSelected.value)
        )
        // No zones selected = "no real-time alerts". The zone table is opt-out (an empty
        // set means ALL zones, incl. future ones), so "off" cannot be expressed by zones —
        // it lives on the delivery masters. noZones drives the grey-out + the save below.
        const noZones = computed(() => zoneSelected.value.size === 0)
        // Coming back from the None/off state — default the in-portal pop ON so a freshly
        // picked zone actually reaches the user (push needs a device, so leave it as-is).
        const _ensureDelivery = () => {
            if (!realtimeToast.value && !realtimePush.value) realtimeToast.value = true
        }
        const zoneChecked = (id) => zoneSelected.value.has(id)
        const toggleZone = (id) => {
            const s = new Set(zoneSelected.value)
            s.has(id) ? s.delete(id) : s.add(id)
            zoneSelected.value = s
            if (s.size) _ensureDelivery()
            else { realtimeToast.value = false; realtimePush.value = false }   // last zone off = no alerts
        }
        const selectAllZones = () => { zoneSelected.value = new Set(allZones.value.map(z => z.id)); _ensureDelivery() }
        // "None" turns real-time alerts OFF: clear the zones AND flip the delivery masters
        // off (the persisted "off" — empty zones alone silently mean ALL via opt-out).
        const clearZones = () => { zoneSelected.value = new Set(); realtimeToast.value = false; realtimePush.value = false }

        const loadZoneSubscriptions = async () => {
            const all = new Set(allZones.value.map(z => z.id))
            try {
                const zs = await userStore.fetchZoneSubscriptions()
                // not customised → all zones selected (the opt-out default)
                zoneSelected.value = zs.customised ? new Set(zs.subscribedZoneIds) : all
            } catch (e) {
                console.error('Failed to load zone subscriptions', e)
                zoneSelected.value = all
            }
            originalZoneSelected.value = new Set(zoneSelected.value)
        }

        const saveRealtime = async () => {
            if (!isRealtimeChanged.value || isSavingRealtime.value) return
            isSavingRealtime.value = true
            try {
                // No zones = no alerts: persist "off" on the delivery masters, because an
                // empty zone set alone means ALL via the opt-out default (see clearZones).
                const effToast = noZones.value ? false : realtimeToast.value
                const effPush  = noZones.value ? false : realtimePush.value
                await userStore.updateZoneSubscriptions([...zoneSelected.value])
                if (effToast !== originalRealtimeToast.value ||
                    effPush !== originalRealtimePush.value) {
                    await userStore.updatePreferences({
                        RealtimeToast: effToast,
                        RealtimePush: effPush,
                    })
                }
                realtimeToast.value = effToast
                realtimePush.value = effPush
                originalRealtimeToast.value = effToast
                originalRealtimePush.value = effPush
                originalZoneSelected.value = new Set(zoneSelected.value)
                toast.success(i18n.global.t('settings.realtime.saved'))
            } catch (e) {
                console.error('Failed to save real-time alerts', e)
                toast.error(i18n.global.t('settings.realtime.saveFailed'))
            } finally {
                isSavingRealtime.value = false
            }
        }

        // ---------- Push notifications (docs/15 C) ----------
        // Device-level: can this browser be reached by push at all. The composable
        // owns the service-worker + permission + subscribe dance and its own
        // reactive state (permission / subscribed / busy).
        const pushApi = usePush()
        const pushSupported = pushApi.isSupported()
        const pushNeedsInstall = pushApi.needsIOSInstall()   // iOS-not-installed
        const pushIsStandalone = pushApi.isStandalone()      // already installed?

        // When the app CAN be installed but hasn't been, installing is step 1 and
        // enabling push is step 2 — showing both as equal green buttons made people
        // bounce between the install dialog and the notification-permission prompt.
        // This gates the enable control behind install (muted, not blocked: desktop
        // users who skip install can still turn it on).
        const pushShouldInstallFirst = computed(() =>
            pushApi.canInstall.value && !pushIsStandalone && !pushApi.installed.value)

        // Chrome's own install banner flashes past and auto-dismisses, so we fire
        // the real dialog from a button instead (usePush captured the event).
        const installApp = async () => {
            const outcome = await pushApi.promptInstall()
            if (outcome === 'accepted') toast.success(i18n.global.t('settings.push.toastInstalled'))
            else if (outcome === 'dismissed') toast.info(i18n.global.t('settings.push.toastInstallDismissed'))
            else toast.info(i18n.global.t('settings.push.toastInstallUnavailable'))
        }

        const enableDeviceNotifications = async () => {
            try {
                await pushApi.enable()
                toast.success(i18n.global.t('settings.push.toastEnabled'))
            } catch (e) {
                toast.error(e.message || i18n.global.t('settings.push.toastEnableFailed'))
            }
        }
        const disableDeviceNotifications = async () => {
            try {
                await pushApi.disable()
                toast.info(i18n.global.t('settings.push.toastDisabled'))
            } catch (e) {
                toast.error(i18n.global.t('settings.push.toastDisableFailed'))
            }
        }

        // Allen push (admin-only opt-in) — separate from the device subscription.
        const coachPush = ref(false)
        const isSavingCoachPush = ref(false)

        // Re-subscribe nudge: a push subscription silently dies when the user clears
        // browser data (the service worker + PushManager subscription go with it),
        // with no signal anywhere — the device just goes dark on all channels. Detect
        // the mismatch (a push preference is ON for the account, but THIS device holds
        // no live subscription) and surface a clear re-enable prompt. Excludes the
        // denied / iOS-not-installed / install-first states, which have their own copy.
        const pushNeedsReenable = computed(() =>
            pushSupported
            && !pushApi.subscribed.value
            && pushApi.permission.value !== 'denied'
            && !pushNeedsInstall
            && !pushShouldInstallFirst.value
            && (coachPush.value || realtimePush.value))
        const toggleCoachPush = async () => {
            isSavingCoachPush.value = true
            try {
                coachPush.value = await userStore.setCoachPush(coachPush.value)
                toast.success(i18n.global.t('settings.push.saved'))
            } catch (e) {
                coachPush.value = !coachPush.value       // revert on failure
                toast.error(i18n.global.t('settings.push.saveFailed'))
            } finally {
                isSavingCoachPush.value = false
            }
        }

        // ---------- Company Benchmark — Retail Sector picker state ----------
        // Display labels for the 8 sectors. SECTORS (from ceilingsRef.js) is
        // the canonical list; this dictionary just maps each key to a human
        // label for the dropdown. Add a new sector here whenever ceilingsRef
        // grows.
        // Labels live in locales/*/settings.json (benchmark.sectors.<key>).
        const sectorLabel = (s) => {
            const key = `settings.benchmark.sectors.${s}`
            return i18n.global.te(key, 'en') ? i18n.global.t(key) : s
        }
        const sectorOptions = computed(() => SECTORS.map(s => ({
            value: s,
            label: sectorLabel(s),
        })))

        // One-sentence retail context per sector — shown above the
        // ceilings preview so the admin understands what kind of retail
        // they're picking before saving. Deliberately number-free: the
        // ceilings preview below shows the per-sector E_max / C_max /
        // TWT values, and mixing store-level conversion percentages here
        // would look inconsistent with those zone-level numbers.
        // Texts live in locales/*/settings.json (benchmark.sectorDescriptions.<key>).
        const sectorDescriptionOf = (s) => {
            const key = `settings.benchmark.sectorDescriptions.${s}`
            return i18n.global.te(key, 'en') ? i18n.global.t(key) : ''
        }

        // selectedSector: current dropdown value (the unsaved choice).
        // originalSector: last persisted value, for change detection.
        // Empty string means "no sector configured yet" → save button stays
        // disabled until an explicit choice is made.
        const selectedSector = ref('')
        const originalSector = ref('')
        const isSavingSector = ref(false)

        const isSectorChanged = computed(() =>
            !!selectedSector.value && selectedSector.value !== originalSector.value
        )

        // Live preview of the starting ceilings the dropdown choice will pull
        // in once saved. Reads from the shared ceilingsRef so it stays in
        // sync with whatever the analytics engine will actually apply.
        const sectorPreview = computed(() => {
            if (!selectedSector.value) return null
            const ceil = getZoneTypeDefault(selectedSector.value, 'product')
            const twt  = getTwt(selectedSector.value)
            return {
                label: sectorLabel(selectedSector.value),
                Emax: ceil.Emax.value,
                Cmax: ceil.Cmax.value,
                twt:  twt.value,
            }
        })

        // Human-readable retail context for the currently-selected sector.
        // Shown above the ceilings preview so the admin sees *why* a
        // sector implies its specific E_max / C_max / TWT defaults before
        // hitting Save.
        const sectorDescription = computed(() =>
            selectedSector.value
                ? sectorDescriptionOf(selectedSector.value)
                : ''
        )

        // €/hr figure: same digits as typed in English, Dutch decimal comma in Dutch.
        function fmtCost(n) {
            const decimals = (String(n).split('.')[1] || '').length
            return formatNumber(n, { decimals })
        }

        function formatPct(v) {
            if (typeof v !== 'number' || !isFinite(v)) return '—'
            return `${Math.round(v * 100)}%`
        }

        async function saveSector() {
            if (!isSectorChanged.value || isSavingSector.value) return
            isSavingSector.value = true
            try {
                await companyStore.updateCompanySettings({ sector: selectedSector.value })
                originalSector.value = selectedSector.value
                toast.success(i18n.global.t('settings.benchmark.sectorSaved'))
            } catch (err) {
                console.error('Failed to save sector', err)
                toast.error(i18n.global.t('settings.benchmark.sectorSaveFailed'))
            } finally {
                isSavingSector.value = false
            }
        }

        // ---------- Checkout staff cost override ----------
        // staffCostInput holds the raw input value as a string ('' = no
        // override, "30" = override €30/hr). Storing as string keeps
        // the input bound cleanly to v-model and makes the empty-vs-
        // zero distinction explicit (zero would round-trip back as
        // "no override" via the validity check below).
        const staffCostInput = ref('')
        const originalStaffCost = ref('')
        const isSavingStaffCost = ref(false)

        // Sector default — tracks the currently-selected sector so
        // the placeholder + effective-rate preview stay in sync as the
        // admin picks sectors. Falls back to the 'default' bucket
        // when no sector is selected yet.
        const sectorDefaultStaffCost = computed(() => {
            const sector = selectedSector.value || 'default'
            return (
                CAPACITY_VALUE_SETTINGS[sector]?.staff_cost_per_hour
                ?? CAPACITY_VALUE_SETTINGS.default.staff_cost_per_hour
            )
        })

        // Parsed override value (number) — only counts when it's a
        // positive finite number. Anything else (empty / zero /
        // non-numeric) means "no override".
        const parsedStaffCost = computed(() => {
            const v = Number(staffCostInput.value)
            return (Number.isFinite(v) && v > 0) ? v : null
        })

        const isStaffCostChanged = computed(() => {
            // Normalise both sides for comparison: empty string ↔ ''.
            // Matters when the user types and clears the field — both
            // forms should be considered "unchanged from the empty
            // baseline".
            return String(staffCostInput.value ?? '') !== String(originalStaffCost.value ?? '')
        })

        async function saveStaffCost() {
            if (!isStaffCostChanged.value || isSavingStaffCost.value) return
            isSavingStaffCost.value = true
            try {
                // Send the parsed number when set, null when cleared
                // (null in the merge clears the key — frontend reads
                // it back as "no override" → sector default).
                await companyStore.updateCompanySettings({
                    staffCostPerHour: parsedStaffCost.value,
                })
                originalStaffCost.value = staffCostInput.value
                toast.success(i18n.global.t('settings.benchmark.staffCostSaved'))
            } catch (err) {
                console.error('Failed to save staff cost', err)
                toast.error(i18n.global.t('settings.benchmark.staffCostSaveFailed'))
            } finally {
                isSavingStaffCost.value = false
            }
        }

        // Combined save for the merged "Store Benchmark" card — one action for
        // both sector and staff-cost, so the tab has one Save, not two. Sends
        // only the fields that actually changed (updateCompanySettings merges).
        const isSavingBenchmark = ref(false)
        const isBenchmarkChanged = computed(() =>
            isSectorChanged.value || isStaffCostChanged.value
        )
        async function saveBenchmark() {
            if (!isBenchmarkChanged.value || isSavingBenchmark.value) return
            isSavingBenchmark.value = true
            try {
                const patch = {}
                if (isSectorChanged.value)    patch.sector = selectedSector.value
                if (isStaffCostChanged.value) patch.staffCostPerHour = parsedStaffCost.value
                await companyStore.updateCompanySettings(patch)
                originalSector.value = selectedSector.value
                originalStaffCost.value = staffCostInput.value
                toast.success(i18n.global.t('settings.benchmark.saved'))
            } catch (err) {
                console.error('Failed to save benchmark', err)
                toast.error(i18n.global.t('settings.benchmark.saveFailed'))
            } finally {
                isSavingBenchmark.value = false
            }
        }
        // -------------------------------------------------------------------

        // ---------- Allen coach: master enable switch (CompanySettings.coach_enabled) ----------
        const coachEnabled = ref(false)
        const isSavingEnabled = ref(false)
        async function saveCoachEnabled() {
            isSavingEnabled.value = true
            try {
                await companyStore.updateCompanySettings({ coach_enabled: coachEnabled.value })
                toast.success(coachEnabled.value ? i18n.global.t('settings.coach.enabled') : i18n.global.t('settings.coach.disabled'))
            } catch (err) {
                console.error('Failed to update coach_enabled', err)
                toast.error(i18n.global.t('settings.coach.updateFailed'))
                coachEnabled.value = !coachEnabled.value   // revert on failure
            } finally {
                isSavingEnabled.value = false
            }
        }

        // ---------- Allen coach: store context (CompanySettings.coachProfile) ----------
        const coachContext = ref('')
        const coachTone = ref('balanced')
        const originalCoachContext = ref('')
        const originalCoachTone = ref('balanced')
        const isSavingProfile = ref(false)

        const isProfileChanged = computed(() =>
            coachContext.value !== originalCoachContext.value ||
            coachTone.value !== originalCoachTone.value
        )

        async function saveCoachProfile() {
            if (!isProfileChanged.value || isSavingProfile.value) return
            isSavingProfile.value = true
            try {
                // Shallow-merges coachProfile into CompanySettings — sector and
                // staffCostPerHour are preserved (see updateCompanySettings).
                await companyStore.updateCompanySettings({
                    coachProfile: { context: coachContext.value, tone: coachTone.value },
                })
                originalCoachContext.value = coachContext.value
                originalCoachTone.value = coachTone.value
                toast.success(i18n.global.t('settings.coach.contextSaved'))
            } catch (err) {
                console.error('Failed to save coach profile', err)
                toast.error(i18n.global.t('settings.coach.contextSaveFailed'))
            } finally {
                isSavingProfile.value = false
            }
        }

        // ---------- Allen coach: goals (store_goals table) ----------
        const newGoalMetric = ref('')   // '' | 'engagement' | 'conversion' | 'reach'
        const newGoalZone = ref('')     // '' (store-wide) | <product zone name>
        const isAddingGoal = ref(false)
        const activeGoals = computed(() =>
            (companyStore.goals || []).filter(g => g.Status === 'active')
        )

        // Product zones (DetectionZoneTypeId 6) — the zones the engagement /
        // conversion / reach levers apply to, so the coach can reconcile a goal's
        // metric against the zone's recommended lever. Deduped by name.
        const productZones = computed(() => {
            const names = (detectionZoneStore.detectionzones || [])
                .filter(z => z.DetectionZoneTypeId === 6)
                .map(z => z.DetectionZoneName)
                .filter(Boolean)
            return [...new Set(names)].sort()
        })

        // Reach + engagement apply to all commercial polygon zones (Product 6,
        // Demo 7, Display 8); conversion only to Product (sales are mapped there
        // only). Store traffic is store-level (no zone).
        const commercialZones = computed(() => {
            const names = (detectionZoneStore.detectionzones || [])
                .filter(z => [6, 7, 8].includes(z.DetectionZoneTypeId))
                .map(z => z.DetectionZoneName)
                .filter(Boolean)
            return [...new Set(names)].sort()
        })
        const goalZoneOptions = computed(() => {
            const m = newGoalMetric.value
            if (m === 'conversion') return productZones.value
            if (m === 'reach' || m === 'engagement') return commercialZones.value
            return []
        })
        // A metric change can invalidate the chosen zone (a demo zone under a
        // conversion goal, or any zone under store-level traffic) — reset it.
        watch(newGoalMetric, () => {
            if (newGoalMetric.value === 'traffic') { newGoalZone.value = ''; return }
            if (newGoalZone.value && !goalZoneOptions.value.includes(newGoalZone.value)) {
                newGoalZone.value = ''
            }
        })

        // The English title addGoal() stores for a metric + zone.
        const derivedGoalTitle = (metric, zone) => metric === 'traffic'
            ? 'Grow store traffic'
            : `Grow ${metric}` + (zone ? ` in ${zone}` : ' store-wide')
        const goalMetricLabel = (metric) => {
            const key = `settings.goals.metrics.${metric}`
            return metric && i18n.global.te(key, 'en') ? i18n.global.t(key) : metric
        }
        // A title the portal wrote itself is shown in the reader's language; a
        // title typed elsewhere is shown as written.
        const goalTitle = (goal) => {
            const { title, metric, zone } = goal.GoalData || {}
            if (!title) return i18n.global.t('settings.goals.untitled')
            if (!metric || title !== derivedGoalTitle(metric, zone)) return title
            if (metric === 'traffic') return i18n.global.t('settings.goals.titles.traffic')
            const m = goalMetricLabel(metric)
            return zone
                ? i18n.global.t('settings.goals.titles.inZone', { metric: m, zone })
                : i18n.global.t('settings.goals.titles.storeWide', { metric: m })
        }

        async function addGoal() {
            const metric = newGoalMetric.value
            if (!metric || isAddingGoal.value) return
            isAddingGoal.value = true
            try {
                const zone = newGoalZone.value
                // Title is derived from the structured choice — one source of truth.
                // Saved in English (it is data Allen reads); goalTitle() shows it translated.
                const title = derivedGoalTitle(metric, zone)
                const goalData = { title, metric }
                if (zone && metric !== 'traffic') goalData.zone = zone
                await companyStore.createGoal(goalData)
                newGoalMetric.value = ''
                newGoalZone.value = ''
                toast.success(i18n.global.t('settings.goals.added'))
            } catch (err) {
                console.error('Failed to add goal', err)
                toast.error(i18n.global.t('settings.goals.addFailed'))
            } finally {
                isAddingGoal.value = false
            }
        }

        async function achieveGoal(goalId) {
            try {
                await companyStore.updateGoal(goalId, { Status: 'achieved' })
                toast.success(i18n.global.t('settings.goals.achieved'))
            } catch (err) {
                console.error('Failed to update goal', err)
                toast.error(i18n.global.t('settings.goals.updateFailed'))
            }
        }

        // ---------- Allen coach: durable memory (conversation_memory) ----------
        const newFact = ref('')
        const isAddingFact = ref(false)
        const coachFacts = computed(() => companyStore.memory || [])

        async function addFact() {
            const fact = newFact.value.trim()
            if (!fact || isAddingFact.value) return
            isAddingFact.value = true
            try {
                const res = await companyStore.addCoachMemory(fact)
                newFact.value = ''
                toast.success(res && res.status === 'duplicate'
                    ? i18n.global.t('settings.memory.alreadyKnew') : i18n.global.t('settings.memory.willRemember'))
            } catch (err) {
                console.error('Failed to add fact', err)
                toast.error(i18n.global.t('settings.memory.saveFailed'))
            } finally {
                isAddingFact.value = false
            }
        }

        async function forgetFact(memoryId) {
            try {
                await companyStore.forgetCoachMemory(memoryId)
                toast.success(i18n.global.t('settings.memory.forgot'))
            } catch (err) {
                console.error('Failed to forget fact', err)
                toast.error(i18n.global.t('settings.memory.forgetFailed'))
            }
        }
        // -------------------------------------------------------------------

        const loadingComplete = ref(false)
        const isLoading= ref(false)

        // Load this company's members. Kept in one place: the table is rebuilt
        // after add/remove too.
        const loadMembers = async () => {
            const members = await userStore.fetchCompanyMembers(
                userStore.currentCompany.CompanyId)
            userList.value = members.map(u => ({
                ...u, editing: false, emailTouched: false,
            }))
        }

        onBeforeMount(async () => {
            await userStore.fetchUserDetails()
            await companyStore.fetchCompanyDetails(userStore.currentCompany.CompanyId)

            // The team = this company's MEMBERS (with the role they hold here).
            // Was companyStore.company.users — the FK list, i.e. whoever happened
            // to be switched to this company, so a store could show an empty team
            // while having members.
            await loadMembers()

            // get_user_details returns a single `preferences` object (defaults
            // already applied) covering both the mail-report toggles and the
            // Allen notification-email prefs. Split it into the two cards' state.
            const prefs = userStore.user?.preferences || {}

            const merged = {
                SendEndOfDay:   !!prefs.SendEndOfDay,
                SendEndOfWeek:  !!prefs.SendEndOfWeek,
                SendEndOfMonth: !!prefs.SendEndOfMonth,
            }
            userSettings.value = merged
            originalUserSettings.value = JSON.parse(JSON.stringify(merged)) // snapshot for change-detection

            const loadedPrefs = {
                CoachEmailEnabled: !!prefs.CoachEmailEnabled,
                CoachEmailHigh:    prefs.CoachEmailHigh !== false,
                CoachEmailWarning: prefs.CoachEmailWarning !== false,
                CoachEmailGood:    !!prefs.CoachEmailGood,
            }
            notifyPrefs.value = loadedPrefs
            originalNotifyPrefs.value = JSON.parse(JSON.stringify(loadedPrefs))

            // Hydrate the sector picker from the freshly-loaded company.
            // CompanyStore parses CompanySettings JSON for us, so the field
            // is already an object (or {} when never configured).
            const persistedSector = companyStore.company?.CompanySettings?.sector || ''
            selectedSector.value = persistedSector
            originalSector.value = persistedSector

            // Hydrate the staff-cost override. Stored as a number
            // when set; converted to its string form for v-model.
            // Empty string = no override (input shows the sector
            // default as placeholder).
            const persistedStaffCost = companyStore.company?.CompanySettings?.staffCostPerHour
            const persistedStaffCostStr =
                (typeof persistedStaffCost === 'number' && persistedStaffCost > 0)
                    ? String(persistedStaffCost)
                    : ''
            staffCostInput.value = persistedStaffCostStr
            originalStaffCost.value = persistedStaffCostStr

            // Hydrate the coach profile (CompanySettings.coachProfile) and load
            // this company's goals from the store_goals table.
            coachEnabled.value = !!companyStore.company?.CompanySettings?.coach_enabled
            const coachProfile = companyStore.company?.CompanySettings?.coachProfile || {}
            coachContext.value = coachProfile.context || ''
            coachTone.value = coachProfile.tone || 'balanced'
            originalCoachContext.value = coachContext.value
            originalCoachTone.value = coachTone.value
            try {
                await companyStore.fetchGoals()
            } catch (err) {
                console.error('Failed to load coach goals', err)
            }
            try {
                await companyStore.fetchCoachMemory()
            } catch (err) {
                console.error('Failed to load coach memory', err)
            }
            // Product zones for the goal focus/zone picker (best-effort). Also the
            // source for the real-time zone picker below, so load subscriptions AFTER.
            try {
                await detectionZoneStore.fetchDetectionZones()
            } catch (err) {
                console.error('Failed to load zones for goal picker', err)
            }

            // Real-time alerts: the in-portal pop toggle + the per-user zone opt-in.
            realtimeToast.value = prefs.RealtimeToast !== false      // default on
            originalRealtimeToast.value = realtimeToast.value
            realtimePush.value = prefs.RealtimePush === true         // default off
            originalRealtimePush.value = realtimePush.value
            await loadZoneSubscriptions()
            // "No alerts" is stored on the delivery masters (the opt-out zone table can't
            // hold an empty set — no rows = ALL). So if BOTH channels are off, render the
            // scope as None + grey the delivery block, matching what the user saved.
            if (!realtimeToast.value && !realtimePush.value) {
                zoneSelected.value = new Set()
                originalZoneSelected.value = new Set()
            }

            // Push: the admin Allen-push flag + whether this device is subscribed.
            coachPush.value = !!prefs.CoachPushEnabled
            if (pushSupported) { try { await pushApi.refresh() } catch (_) { /* best-effort */ } }

            // Mark loading as complete to enable watchers
            loadingComplete.value = true
        })

        watch(
            userSettings,
            (newVal) => {
                if (!loadingComplete.value) return

                const norm = (u) => ({
                SendEndOfDay: !!u.SendEndOfDay,
                SendEndOfWeek: !!u.SendEndOfWeek,
                SendEndOfMonth: !!u.SendEndOfMonth,
                })

                isMailSettingsChanged.value =
                JSON.stringify(norm(newVal)) !== JSON.stringify(norm(originalUserSettings.value))
            },
            { deep: true }
        )

        const saveMailSettings = async () => {
            try {
                isSavingMailSettings.value = true
                // Self-service prefs go through the merge-safe /user/preferences
                // endpoint (not PUT /users/<id>, which is for admins editing other
                // users and never persists these keys). Send only the mail toggles.
                await userStore.updatePreferences({
                    SendEndOfDay:   userSettings.value.SendEndOfDay,
                    SendEndOfWeek:  userSettings.value.SendEndOfWeek,
                    SendEndOfMonth: userSettings.value.SendEndOfMonth,
                })
                originalUserSettings.value = JSON.parse(JSON.stringify(userSettings.value))
                isMailSettingsChanged.value = false
                toast.success(i18n.global.t('settings.email.mailSaved'))
            } catch (e) {
                console.error(e)
                toast.error(i18n.global.t('settings.email.mailSaveFailed'))
            } finally {
                isSavingMailSettings.value = false
            }
        }
        
        
        const toggleAddUser = () => {
            if (addingUser.value && newUserName.value && newUserSurname.value && newUserEmail.value) {
                userList.value.push({
                UserName: newUserName.value,
                UserSurname: newUserSurname.value,
                UserEmail: newUserEmail.value,
                UserRole: newUserRole.value,
                })
                newUserName.value = ''
                newUserSurname.value = ''
                newUserEmail.value = ''
                newUserRole.value = 'User'
            }
            addingUser.value = !addingUser.value
        }

        const cancelAddUser = () => {
            addingUser.value = false
            newUserName.value = ''
            newUserSurname.value = ''
            newUserEmail.value = ''
            newUserRole.value = 'User'
            newUserEmailTouched.value = false
        };

        const addUser = async () => {
            try {
                isLoading.value = true
                userData.value = {
                    UserName: newUserName.value,
                    UserSurname: newUserSurname.value,
                    UserEmail: newUserEmail.value,
                    CompanyId: companyStore.company.CompanyId,
                    UserRole: newUserRole.value
                }

                const res = await userStore.addUser(userData.value)
                // A super-admin typing an email that already has an account gets
                // it LINKED to this company rather than duplicated — and no
                // set-password mail goes out, so don't imply one did.
                toast.success(res?.linkedExistingUser
                    ? i18n.global.t('settings.team.linkedExisting')
                    : i18n.global.t('settings.team.added'))

                // Add the new user to the list
                userList.value.push({
                    UserName: newUserName.value,
                    UserSurname: newUserSurname.value,
                    UserEmail: newUserEmail.value,
                    UserRole: newUserRole.value,
                    editing: false,
                    emailTouched: false
                })

                // Reset form data after successful registration
                newUserName.value = ''
                newUserSurname.value = ''
                newUserEmail.value = ''
                newUserRole.value = 'User'
                newUserEmailTouched.value = false

                isLoading.value = false

            } catch (error) {
                console.error("An unexpected error occurred while adding the user to the database.:", error)
                toast.error(i18n.global.t('settings.team.addFailed'), error)
                isLoading.value = false
            }
        }

        const editUser = (user) => {
            user.editing = true
            user.originalData = { ...user } // Store a copy of the original data
        }

        const hasChanges = (user) => {
            return (
                user.UserName !== user.originalData.UserName ||
                user.UserSurname !== user.originalData.UserSurname ||
                user.UserEmail !== user.originalData.UserEmail ||
                user.UserRole !== user.originalData.UserRole
            )
        }

        const cancelEditUser = (user) => {
            user.editing = false
            user.emailTouched = false
        }

        const updateUser = async (userId) => {
            isLoading.value = true
            const user = userList.value.find(u => u.UserId === userId)
            if (!user) return
            try {
                await userStore.updateUser(user)
                toast.success(i18n.global.t('settings.team.updated'))
                user.editing = false
                isLoading.value = false
            } catch (error) {
                console.error("An unexpected error occurred while updating the user in the database.:", error)
                toast.error(i18n.global.t('settings.team.updateFailed'))
                isLoading.value = false
            }
        };

        const deleteUser = async (userEmail) => {
            try{
                if (confirm(i18n.global.t('settings.team.confirmDelete'))) {
                    isLoading.value = true
                    await userStore.deleteUser(userEmail)
                    // Update userList after successful deletion
                    userList.value = userList.value.filter(user => user.UserEmail !== userEmail)
                    toast.success(i18n.global.t('settings.team.deleted'))
                    isLoading.value = false
                }
            } catch (error) {
                console.error("An unexpected error occurred while deleting the user from the database.:", error)
                toast.error(i18n.global.t('settings.team.deleteFailed'), error)
                isLoading.value = false
            }
        }

        // Re-send the tokenised "set your password" invitation email to an
        // existing user (backend POST /users/<id>/resend-invite). resendingUserId
        // tracks the row currently in-flight so its button can show a spinner.
        const resendingUserId = ref(null)
        const resendInvite = async (user) => {
            if (resendingUserId.value) return
            resendingUserId.value = user.UserId
            try {
                await userStore.resendInvite(user.UserId)
                toast.success(i18n.global.t('settings.team.inviteResent', { email: user.UserEmail }))
            } catch (err) {
                toast.error(userStore.error || i18n.global.t('settings.team.inviteResendFailed'))
            } finally {
                resendingUserId.value = null
            }
        }

        const isEmailValid = (email) => {
            return email !== '' && /\S+@\S+\.\S+/.test(email)
        }

        // ---------- Add an EXISTING Storalytic user to this company ----------
        // Super-admin only (backend enforces it too). This is the other half of
        // multi-company support: it's what previously required hand-editing the
        // access[] JSON to let one person work across two stores.
        const addingExisting   = ref(false)
        const existingEmail    = ref('')
        const existingRole     = ref('Key User')
        const isAddingExisting = ref(false)

        const toggleAddExisting = () => {
            addingExisting.value = true
            existingEmail.value = ''
            existingRole.value = 'Key User'
        }
        const cancelAddExisting = () => {
            addingExisting.value = false
            existingEmail.value = ''
        }
        const addExistingMember = async () => {
            if (!isEmailValid(existingEmail.value) || isAddingExisting.value) return
            isAddingExisting.value = true
            try {
                const res = await userStore.addExistingMember(
                    userStore.currentCompany.CompanyId,
                    existingEmail.value.trim(),
                    existingRole.value)
                toast.success(res.message || i18n.global.t('settings.team.addedToCompany'))
                cancelAddExisting()
                await loadMembers()
            } catch (err) {
                toast.error(err.message || i18n.global.t('settings.team.addExistingFailed'))
            } finally {
                isAddingExisting.value = false
            }
        }

        // Which settings section is showing in the content pane. Groups:
        // 'store' (device, benchmark, staff cost, opening hours, location & calendar),
        // 'allen' (coach goals/context/memory + mail reports),
        // 'team'  (user management).
        const activeSection = ref('store')
        const SECTIONS = [
            { id: 'store', label: 'Store',           icon: 'bi-shop',        adminOnly: true  },
            { id: 'allen', label: 'Allen & Reports', icon: 'bi-stars',       adminOnly: false },
            { id: 'team',  label: 'Team',            icon: 'bi-people-fill', adminOnly: true  },
        ]
        const isAdmin = computed(() => userStore.currentCompany?.role === 'Admin')
        // Role VALUES stay English (sent to the backend); only the label is translated.
        const ROLE_KEYS = { 'Admin': 'admin', 'Key User': 'keyUser', 'User': 'user' }
        const roleLabel = (role) => ROLE_KEYS[role] ? i18n.global.t(`settings.roles.${ROLE_KEYS[role]}`) : role

        // Store language (Companies.DefaultLanguage). Not set = English.
        const savedStoreLanguage = computed(() => userStore.user?.companyDefaultLanguage || 'en')
        const storeLanguage = ref(savedStoreLanguage.value)
        watch(savedStoreLanguage, (v) => { storeLanguage.value = v })
        const isSavingLanguage = ref(false)
        const isStoreLanguageChanged = computed(() => storeLanguage.value !== savedStoreLanguage.value)
        const saveStoreLanguage = async () => {
            isSavingLanguage.value = true
            try {
                await userStore.setCompanyDefaultLanguage(storeLanguage.value)
                toast.success(i18n.global.t('storeLanguage.saved'))
            } catch (e) {
                toast.error(i18n.global.t('storeLanguage.saveFailed'))
            } finally {
                isSavingLanguage.value = false
            }
        }

        const visibleSections = computed(() =>
            SECTIONS.filter(s => !s.adminOnly || isAdmin.value)
        )
        // Keep the active pane valid: a non-admin never lands on an admin-only
        // section (they only see Allen & Reports).
        watch(visibleSections, (secs) => {
            if (secs.length && !secs.some(s => s.id === activeSection.value)) {
                activeSection.value = secs[0].id
            }
        }, { immediate: true })

        const backHome = () => {
            router.push('/')
        }

        const onCancel = () => {
            console.log('User cancelled the loader.')
        }

        return {
            userStore,
            companyStore,
            userSettings,
            isMailSettingsChanged,
            isSavingMailSettings,
            saveMailSettings,
            // Allen notification-email preferences
            notifyPrefs,
            isNotifyPrefsChanged,
            isSavingNotifyPrefs,
            saveNotifyPrefs,
            // unified email-preferences save (reports + notifications)
            isEmailPrefsChanged,
            isSavingEmailPrefs,
            saveEmailPrefs,
            // real-time alerts (docs/15 channel 2)
            realtimeToast,
            realtimePush,
            noZones,
            allZones,
            zoneChecked,
            toggleZone,
            selectAllZones,
            clearZones,
            isRealtimeChanged,
            isSavingRealtime,
            saveRealtime,
            // push notifications (docs/15 C)
            pushSupported,
            pushNeedsInstall,
            pushIsStandalone,
            pushCanInstall: pushApi.canInstall,
            pushInstalled: pushApi.installed,
            pushShouldInstallFirst,
            pushNeedsReenable,
            installApp,
            pushPermission: pushApi.permission,
            pushSubscribed: pushApi.subscribed,
            pushBusy: pushApi.busy,
            enableDeviceNotifications,
            disableDeviceNotifications,
            coachPush,
            isSavingCoachPush,
            toggleCoachPush,
            // Company Benchmark Sector picker
            sectorOptions,
            selectedSector,
            isSectorChanged,
            isSavingSector,
            sectorPreview,
            sectorDescription,
            saveSector,
            isBenchmarkChanged,
            isSavingBenchmark,
            saveBenchmark,
            formatPct,
            fmtCost,
            // Checkout Staff Cost override
            staffCostInput,
            sectorDefaultStaffCost,
            isStaffCostChanged,
            isSavingStaffCost,
            saveStaffCost,
            // Allen Coach — enable switch + context + goals
            coachEnabled,
            isSavingEnabled,
            saveCoachEnabled,
            coachContext,
            coachTone,
            isProfileChanged,
            isSavingProfile,
            saveCoachProfile,
            newGoalMetric,
            newGoalZone,
            productZones,
            goalZoneOptions,
            isAddingGoal,
            activeGoals,
            addGoal,
            achieveGoal,
            goalTitle,
            goalMetricLabel,
            coachFacts,
            newFact,
            isAddingFact,
            addFact,
            forgetFact,
            // ---
            userList,
            addUser,
            editUser,
            hasChanges,
            cancelEditUser,
            updateUser,
            deleteUser,
            resendInvite,
            resendingUserId,
            // add an existing Storalytic user (super-admin only)
            addingExisting,
            existingEmail,
            existingRole,
            isAddingExisting,
            toggleAddExisting,
            cancelAddExisting,
            addExistingMember,
            activeSection,
            visibleSections,
            isAdmin,
            roleLabel,
            LANGUAGES,
            storeLanguage,
            isSavingLanguage,
            isStoreLanguageChanged,
            saveStoreLanguage,
            addingUser,
            newUserName,
            newUserSurname,
            newUserEmail,
            newUserRole,
            toggleAddUser,
            cancelAddUser,
            newUserEmailTouched,
            isEmailValid,
            isLoading,
            backHome,
            onCancel
        }
    }
}


</script>



<style scoped>
/* ===================== Redesigned settings shell ===================== */
.settings-shell {
  max-width: 1080px;
  margin: 0 auto 48px;
  padding: 0 8px;
}

/* Top bar */
.settings-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 2px 20px;
}
.settings-h1 {
  margin: 0;
  font-weight: 800;
  font-size: 1.6rem;
  color: #2b3a45;
  letter-spacing: -0.01em;
}
.settings-sub {
  margin: 4px 0 0;
  color: #6b7a86;
  font-size: 0.92rem;
}

/* Body = nav + pane */
.settings-body {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

/* Left section nav */
.settings-nav {
  flex: 0 0 210px;
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 14px;
  border: none;
  background: transparent;
  border-radius: 10px;
  color: #55606b;
  font-weight: 600;
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
}
.nav-item i {
  font-size: 1.05rem;
  width: 18px;
  text-align: center;
  color: #9aa7b3;
  transition: color 0.12s ease;
}
.nav-item:hover {
  background: #f1f5f9;
  color: #2b3a45;
}
.nav-item.active {
  background: #eaf1f8;
  color: #4F7EB3;
}
.nav-item.active i {
  color: #4F7EB3;
}

/* Content pane */
.settings-pane {
  flex: 1 1 auto;
  min-width: 0;
}
.pane-group {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Redesigned card — flat + light. No overflow clipping: the Opening Hours child
   card (which receives this parent scope on its root) holds datepicker calendar
   popups that must render past the card's bottom edge. */
.setting-card {
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(13, 38, 76, 0.04);
}
.setting-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 16px 12px;
  border-bottom: 1px solid #f0f3f7;
}
/* subtle icon, not a filled chip */
.sc-accent {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: var(--accent, #4F7EB3);
}
.sc-heading { min-width: 0; }
.sc-title {
  margin: 0;
  font-weight: 700;
  font-size: 0.98rem;
  color: #2b3a45;
}
.sc-desc {
  margin: 1px 0 0;
  color: #7c8894;
  font-size: 0.8rem;
  line-height: 1.35;
}
.setting-card-body {
  padding: 16px 18px;
}
/* Breathing room between each question/answer couple inside a card */
.setting-card-body :deep(.block) { margin-bottom: 22px; }
/* Device connection — a muted reference strip, not a full card */
.device-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px dashed #dbe2ea;
  border-radius: 10px;
  background: #fafbfc;
  color: #7c8894;
  font-size: 0.82rem;
}
.device-strip > i { color: #9aa7b3; font-size: 0.95rem; }
.device-strip .device-label { font-weight: 600; color: #6b7a86; }
.device-strip .device-url {
  color: #4F7EB3;
  background: #eef4fa;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 0.8rem;
}
/* lighter inline help everywhere on the page */
.setting-card-body :deep(.field-help) {
  font-size: 0.8rem;
  color: #7c8894;
  line-height: 1.4;
  margin: 2px 0 6px;
}

/* Compact account cluster (top-right) */
.account-chip {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #55606b;
}
.account-chip > i {
  font-size: 1.7rem;
  color: #b3bdc8;
}
.account-meta {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.account-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #2b3a45;
}
.account-link {
  font-size: 0.8rem;
  color: #4F7EB3;
  text-decoration: none;
}
.account-link:hover { text-decoration: underline; }

/* Collapsible sub-section inside a card (native <details>) */
.sub {
  border: 1px solid #eef2f6;
  border-radius: 10px;
  margin-top: 12px;
  background: #fbfcfd;
}
.sub > summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  font-weight: 600;
  color: #425461;
  font-size: 0.9rem;
}
.sub > summary::-webkit-details-marker { display: none; }
.sub > summary .chev {
  margin-left: auto;
  color: #9aa7b3;
  transition: transform 0.15s ease;
}
.sub[open] > summary .chev { transform: rotate(90deg); }
.sub > summary .count {
  color: #8a97a3;
  font-weight: 600;
}
.sub .sub-body { padding: 0 12px 12px; }
.switch-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}
.add-existing {
  margin-top: 14px;
  padding: 12px;
  border: 1px dashed #dbe2ea;
  border-radius: 10px;
  background: #fafbfc;
}
/* checklist rows a touch tighter; .indent nests the severity list under its switch */
.setting-card-body :deep(.checklist) { display: flex; flex-direction: column; gap: 6px; }
.setting-card-body :deep(.checklist.indent) { margin-top: 6px; padding-left: 22px; }
.setting-card-body :deep(.form-check) { margin-bottom: 0; }
.is-muted {
  opacity: 0.5;
  transition: opacity 0.15s ease;
}
/* Step badge for the ordered Install (1) -> Enable (2) push flow */
.step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-right: 6px;
  border-radius: 50%;
  background: #4F7EB3;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
}
/* Re-subscribe nudge: a push subscription silently died (e.g. browser data
   cleared). Warn without alarm — amber left rail, not a red error. */
.needs-reenable {
  border-left: 3px solid #C77700;
  padding-left: 12px;
  opacity: 1 !important;   /* beat .is-muted if both somehow apply */
}
/* iOS install guidance */
.push-hint {
  padding: 10px 12px;
  border: 1px dashed #dbe2ea;
  border-radius: 10px;
  background: #fbfcfd;
}
/* Real-time zone picker */
.zone-actions { font-size: 0.82rem; color: #7c8894; white-space: nowrap; }
.zone-actions .sep { margin: 0 4px; color: #c3ccd6; }
.linklike {
  border: none; background: none; padding: 0; cursor: pointer;
  color: #4F7EB3; font-size: 0.82rem;
}
.linklike:hover { text-decoration: underline; }
.linklike:disabled { color: #b3bdc8; cursor: not-allowed; text-decoration: none; }
.zone-checklist {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px 14px;
  max-height: 240px;
  overflow-y: auto;
  padding: 10px 12px;
  border: 1px solid #eef2f6;
  border-radius: 10px;
  background: #fbfcfd;
}
.zone-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: #425461;
  cursor: pointer;
  min-width: 0;
}
.zone-item span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* small inline spinner for the resend-invite button */
.mini-spin {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #cdd7e1;
  border-top-color: #4F7EB3;
  border-radius: 50%;
  animation: mini-spin-rot 0.7s linear infinite;
}
@keyframes mini-spin-rot { to { transform: rotate(360deg); } }

@media (max-width: 760px) {
  .settings-body { flex-direction: column; }
  .settings-nav {
    position: static;
    flex-direction: row;
    flex: 1 1 auto;
    width: 100%;
    overflow-x: auto;
    gap: 6px;
    padding-bottom: 4px;
  }
  .nav-item { width: auto; white-space: nowrap; }
}

.settings-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 6px solid var(--accent, #4F7EB3);
  background: #fff;
  border: 1px solid #e6edf3;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 3px 12px rgba(13, 38, 76, 0.06);
}
.title {
  margin: 0;
  font-weight: 800;
  color: #425461;
}
.subtitle {
  margin: 2px 0 0;
  color: #6b7a86;
  font-size: 0.9rem;
}
.btn-outline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #4F7EB3;
  color: #4F7EB3;
  background: #fff;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  text-decoration: none;
}
.btn-outline:hover {
  background: #4F7EB3;
  color: #fff;
}
.btn-solid {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  padding: 8px 12px;
  border: none;
  color: #fff;
  cursor: pointer;
}
.btn-solid.ok {
  background: #6C9046;
}
.btn-solid.danger {
  background: #C21500;
}
button:disabled,
.btn-solid:disabled,
.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
  filter: grayscale(40%);
}
.btn-solid:disabled {
  background: #c9cfd6 !important;
  color: #fff !important;
}
.kpi-accent {
  position: relative;
  border: none;
  box-shadow: 0 4px 18px rgba(13, 38, 76, 0.06);
  border-radius: 14px;
  overflow: hidden;
}
.kpi-accent::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 6px;
  background: var(--accent, #4F7EB3);
}
.form-head {
  padding: 12px 16px;
  border-bottom: 1px solid #eef3f7;
  background: #fff;
}
.form-title {
  margin: 0;
  color: #4F7EB3;
  font-weight: 800;
}
.card-body {
  padding: 14px 16px;
}
.form-label {
  font-weight: 700;
  color: #425461;
}
.muted {
  color: #6b7a86;
  margin-top: 4px;
}
.slider-container {
  width: 100%;
}
.slider {
  margin-top: 44px;
  --slider-connect-bg: #B2C149;
  --slider-tooltip-bg: #B2C149;
  --slider-height: 2px;
  --slider-handle-width: 12px;
  --slider-handle-height: 12px;
}
.form-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #eef3f7;
}
.saving {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7a86;
  margin-right: auto;
}
.checklist {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}
/* Sector picker preview pill — single visual unit that combines the
   plain-language retail context with the per-sector benchmark ceilings.
   Description sits on top in a modest italic muted style; the ceiling
   line below has more weight (it's the actual data). */
.sector-preview {
  margin-top: 12px;
  padding: 10px 14px;
  background: #f7faff;
  border: 1px solid #e3ebf7;
  border-radius: 8px;
  color: #425461;
}
.sector-description {
  margin: 0 0 8px;
  font-size: 0.85rem;
  font-style: italic;
  line-height: 1.5;
  color: #6b7a87;
}
.sector-ceilings {
  font-size: 0.85rem;
  line-height: 1.45;
}
.sector-ceilings strong {
  color: #1a2a36;
}

.check-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #425461;
}
/* let the 6-column user table scroll horizontally on narrow screens instead of
   being clipped by the page-level overflow-x:clip (long emails can't wrap). */
.table-container { overflow-x: auto; }
.user-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.user-table th {
  color: #8a97a3;
  text-align: left;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  font-weight: 700;
  padding: 8px 10px;
  border-bottom: 1px solid #e6edf3;
}
.user-table th:last-child { text-align: right; }
.user-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f2f5f7;
  color: #425461;
}

/* Below 955px: stack each row into a card (label | value) instead of scrolling.
   Column order is fixed, so nth-child supplies the labels (no per-cell markup). */
@media (max-width: 955px) {
  .user-table thead {
    position: absolute; width: 1px; height: 1px;
    overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap;
  }
  .user-table, .user-table tbody, .user-table tr, .user-table td { display: block; width: 100%; }
  .user-table tr {
    border: 1px solid #e6edf3; border-radius: 12px;
    padding: 10px; margin: 0 0 14px; background: #fff;
    box-shadow: 0 3px 8px rgba(13,38,76,.05);
  }
  .user-table td {
    border: 0; border-bottom: 1px dashed #eef3f7;
    padding: 8px 6px;
    display: grid; grid-template-columns: 110px 1fr; align-items: center; gap: 8px;
  }
  .user-table td:last-child { border-bottom: 0; }
  .user-table td::before {
    font-weight: 600; color: #425461; font-size: .9rem; min-width: 0;
  }
  .user-table td:nth-child(1)::before { content: "Name"; }
  .user-table td:nth-child(2)::before { content: "Surname"; }
  .user-table td:nth-child(3)::before { content: "Email"; }
  .user-table td:nth-child(4)::before { content: "Role"; }
  .user-table td:nth-child(5)::before { content: "Actions"; }
  .user-table td .form-control,
  .user-table td .form-select { width: 100%; }
}
.form-control.small,
.form-select.small {
  font-size: 0.85rem;
  padding: 6px 8px;
  border-radius: 8px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
/* the buttons live inside nested divs (editing / non-editing / add states) —
   gap the div, not just the cell, so they don't stick together */
.actions div {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
/* destructive action reads as destructive */
.btn-outline.danger {
  border-color: #e0b4b4;
  color: #C21500;
}
.btn-outline.danger:hover {
  background: #C21500;
  border-color: #C21500;
  color: #fff;
}
.loader,
.saver {
  width: 20px;
  aspect-ratio: 1;
  border-radius: 50%;
  background: #B2C149;
  --_m: conic-gradient(#0000 10%, #000),
    linear-gradient(#000 0 0) content-box;
  -webkit-mask: var(--_m);
  mask: var(--_m);
  -webkit-mask-composite: source-out;
  mask-composite: subtract;
  animation: spinner 1s linear infinite;
}
@keyframes spinner {
  to {
    transform: rotate(1turn);
  }
}
.divider-soft {
  border: 0;
  border-top: 1px solid #eef3f7;
  margin: 10px 0;
}
.flex-break {
  height: 10px;
}
.lang-select{ max-width: 240px; }
</style>